import { Injectable, signal } from '@angular/core';
import { LoggerService } from 'src/app/services/logger/logger.service';
import { CallStoreService } from '../call-store/call-store.service';

@Injectable({
  providedIn: 'root',
})
export class MediaStreamService {
  private sharedVideoStream: MediaStream | null = null;
  private isStreamActive = signal<boolean>(false);
  private isCleaningUp = false;
  private pendingCreation: Promise<MediaStream> | null = null;

  constructor(
    private logger: LoggerService,
    private callStore: CallStoreService
  ) {
    window.addEventListener('beforeunload', () => {
      this.cleanupVideoStream();
    });
  }

  getSharedVideoStream(): MediaStream | null {
    return this.sharedVideoStream;
  }

  isVideoStreamActive(): boolean {
    return this.isStreamActive();
  }

  // Create or get existing shared video stream
  async createVideoStream(
    constraints: MediaStreamConstraints
  ): Promise<MediaStream> {
    const deviceChanged = this.hasDeviceChanged(constraints);
    const needsNewStream =
      !this.sharedVideoStream || !this.isStreamActive() || deviceChanged;

    if (this.sharedVideoStream && deviceChanged) {
      this.logger.log('[CALL] media stream - device changed', {
        newConstraints: constraints,
      });
    }

    if (!needsNewStream && this.sharedVideoStream) {
      return this.sharedVideoStream;
    }

    // Coalesce concurrent calls: if a getUserMedia is already in flight, all callers
    // wait for the same result instead of each issuing a separate hardware request.
    // Multiple hardware acquisitions cause the camera indicator to blink repeatedly.
    if (this.pendingCreation) {
      return this.pendingCreation;
    }

    if (this.sharedVideoStream) {
      this.stopVideoStream();
    }

    this.pendingCreation = (async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia(constraints);

        // stopVideoStream() nulls pendingCreation as a cancellation signal.
        // If it fired while getUserMedia was in-flight, stop the acquired tracks
        // immediately — otherwise they become an orphaned stream that keeps the
        // camera indicator on indefinitely.
        if (this.pendingCreation === null) {
          stream.getTracks().forEach(track => track.stop());
          this.sharedVideoStream = null;
          this.isStreamActive.set(false);
          throw new Error('cancelled');
        }

        this.sharedVideoStream = stream;
        this.isStreamActive.set(true);

        this.logger.log('[CALL] media stream - create video stream', {
          message: 'Video stream created successfully',
        });

        return stream;
      } catch (error) {
        if ((error as Error)?.message !== 'cancelled') {
          this.logger.error(
            '[CALL] media stream - create video stream - error',
            error
          );
        }

        this.sharedVideoStream = null;
        this.isStreamActive.set(false);

        throw error;
      } finally {
        this.pendingCreation = null;
      }
    })();

    return this.pendingCreation;
  }

  private hasDeviceChanged(newConstraints: MediaStreamConstraints): boolean {
    if (!this.sharedVideoStream) return true;

    // Check if device ID has changed
    const currentTracks = this.sharedVideoStream.getVideoTracks();
    if (currentTracks.length === 0) return true;

    const currentDeviceId = currentTracks[0].getSettings().deviceId;
    const newDeviceId = newConstraints.video;

    // If new constraints don't specify a device, assume same device
    if (typeof newDeviceId === 'boolean' || !newDeviceId) return false;

    if (typeof newDeviceId === 'object' && 'deviceId' in newDeviceId) {
      const deviceId = newDeviceId.deviceId;
      if (typeof deviceId === 'object' && 'exact' in deviceId) {
        return currentDeviceId !== deviceId.exact;
      }
    }

    return false;
  }

  stopVideoStream(): void {
    this.pendingCreation = null;
    if (this.sharedVideoStream && this.isStreamActive()) {
      this.sharedVideoStream.getTracks().forEach((track) => {
        track.stop();
      });

      this.sharedVideoStream = null;
      this.isStreamActive.set(false);

      this.logger.log('[CALL] media stream - stop video stream');
    }
  }

  // Cleanup video stream (for call end)
  cleanupVideoStream(): void {
    if (this.isCleaningUp) {
      this.logger.log(
        '[CALL] media stream - cleanup already in progress'
      );
      return;
    }

    this.isCleaningUp = true;
    this.logger.log('[CALL] clean video stream');
    this.stopVideoStream();

    this.logger.log('[CALL] media stream - clean video stream');
    this.isCleaningUp = false;
  }
}
